import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { licenseTypeLabel } from '../../../utils/controlCenterMunicipalityLicenses'
import type {
  BoundaryShowAllLevel,
  OmniAssetFilters,
  OmniCategory,
  OmniJobFilters,
  OmniMunicipalityFilters,
  OmniPersonFilters,
} from './controlCenterMapTypes'
import {
  ASSET_KIND_COLOR,
  ASSET_KIND_ORDER,
  BOUNDARY_SHOW_ALL_OPTIONS,
  LICENSE_TYPE_COLOR,
  LICENSE_TYPE_ORDER,
  MUNICIPALITY_LEVEL_COLOR,
  MUNICIPALITY_LEVEL_ORDER,
  jobStatusColor,
  personKindLabel,
  PERSON_KIND_COLOR,
  titleCase,
} from './mapEntityPresentation'
import { toggleInList } from './FilterChip'
import { FilterDropdown } from './FilterDropdown'

function ShowAllOnMapToggle({
  checked,
  onChange,
  ariaLabel,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  ariaLabel: string
}) {
  return (
    <label
      className={`btn btn-xs no-animation gap-1.5 normal-case ${
        checked ? 'btn-primary' : 'btn-ghost border border-base-300'
      }`}
    >
      Show all
      <input
        type="checkbox"
        className="toggle toggle-xs toggle-primary"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        aria-label={ariaLabel}
      />
    </label>
  )
}

function mergedOptions(live: string[], selected: readonly string[]): string[] {
  return [...new Set([...live, ...selected])].filter(Boolean)
}

export type OmniCategoryFilterFieldsProps = {
  category: OmniCategory
  jobFilters: OmniJobFilters
  personFilters: OmniPersonFilters
  assetFilters: OmniAssetFilters
  municipalityFilters: OmniMunicipalityFilters
  onJobFiltersChange: (next: OmniJobFilters) => void
  onPersonFiltersChange: (next: OmniPersonFilters) => void
  onAssetFiltersChange: (next: OmniAssetFilters) => void
  onMunicipalityFiltersChange: (next: OmniMunicipalityFilters) => void
  jobStates: string[]
  jobProjectTypeOptions: string[]
  jobStatusOptions: string[]
  assetStatusOptions: string[]
  municipalityStates: string[]
  showAllJobs: boolean
  showAllPeople: boolean
  showAllAssets: boolean
  onShowAllJobsChange: (next: boolean) => void
  onShowAllPeopleChange: (next: boolean) => void
  onShowAllAssetsChange: (next: boolean) => void
  boundaryShowLevel: BoundaryShowAllLevel
  onBoundaryShowLevelChange: (next: BoundaryShowAllLevel) => void
  boundaryOverlaysLoading?: boolean
}

export function OmniCategoryFilterFields({
  category,
  jobFilters,
  personFilters,
  assetFilters,
  municipalityFilters,
  onJobFiltersChange,
  onPersonFiltersChange,
  onAssetFiltersChange,
  onMunicipalityFiltersChange,
  jobStates,
  jobProjectTypeOptions,
  jobStatusOptions,
  assetStatusOptions,
  municipalityStates,
  showAllJobs,
  showAllPeople,
  showAllAssets,
  onShowAllJobsChange,
  onShowAllPeopleChange,
  onShowAllAssetsChange,
  boundaryShowLevel,
  onBoundaryShowLevelChange,
  boundaryOverlaysLoading = false,
}: OmniCategoryFilterFieldsProps) {
  if (category === 'job') {
    const projectTypes = mergedOptions(jobProjectTypeOptions, jobFilters.projectTypes)
    const states = mergedOptions(jobStates, jobFilters.states)
    const statuses = mergedOptions(jobStatusOptions, jobFilters.statuses)
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        <ShowAllOnMapToggle
          checked={showAllJobs}
          onChange={onShowAllJobsChange}
          ariaLabel="Show all filtered jobs on the map"
        />
        <FilterDropdown
          label="Activity"
          options={(['active', 'inactive'] as const).map((activity) => ({
            value: activity,
            label: titleCase(activity),
          }))}
          selected={jobFilters.activity}
          onToggle={(value) =>
            onJobFiltersChange({
              ...jobFilters,
              activity: toggleInList(
                jobFilters.activity,
                value as (typeof jobFilters.activity)[number],
              ),
            })
          }
        />
        {projectTypes.length > 0 ? (
          <FilterDropdown
            label="Project type"
            options={projectTypes.map((projectType) => ({
              value: projectType,
              label: projectType,
            }))}
            selected={jobFilters.projectTypes}
            onToggle={(value) =>
              onJobFiltersChange({
                ...jobFilters,
                projectTypes: toggleInList(jobFilters.projectTypes, value),
              })
            }
          />
        ) : null}
        {states.length > 0 ? (
          <FilterDropdown
            label="State"
            options={states.map((state) => ({
              value: state,
              label: state,
            }))}
            selected={jobFilters.states}
            onToggle={(value) =>
              onJobFiltersChange({
                ...jobFilters,
                states: toggleInList(jobFilters.states, value),
              })
            }
          />
        ) : null}
        {statuses.length > 0 ? (
          <FilterDropdown
            label="Status"
            options={statuses.map((status) => ({
              value: status,
              label: titleCase(status),
              swatchColor: jobStatusColor(status),
            }))}
            selected={jobFilters.statuses}
            onToggle={(value) =>
              onJobFiltersChange({
                ...jobFilters,
                statuses: toggleInList(jobFilters.statuses, value),
              })
            }
          />
        ) : null}
      </div>
    )
  }

  if (category === 'person') {
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        <ShowAllOnMapToggle
          checked={showAllPeople}
          onChange={onShowAllPeopleChange}
          ariaLabel="Show all filtered people on the map"
        />
        <FilterDropdown
          label="Kind"
          options={(['employee', 'external'] as const).map((kind) => ({
            value: kind,
            label: personKindLabel(kind),
            swatchColor: PERSON_KIND_COLOR[kind],
          }))}
          selected={personFilters.kinds}
          onToggle={(value) =>
            onPersonFiltersChange({
              ...personFilters,
              kinds: toggleInList(
                personFilters.kinds,
                value as (typeof personFilters.kinds)[number],
              ),
            })
          }
        />
        <FilterDropdown
          label="Clocked in"
          options={(['yes', 'no'] as const).map((value) => ({
            value,
            label: value === 'yes' ? 'Yes' : 'No',
          }))}
          selected={personFilters.clockedIn}
          onToggle={(value) =>
            onPersonFiltersChange({
              ...personFilters,
              clockedIn: toggleInList(
                personFilters.clockedIn,
                value as (typeof personFilters.clockedIn)[number],
              ),
            })
          }
        />
      </div>
    )
  }

  if (category === 'asset') {
    const statuses = mergedOptions(assetStatusOptions, assetFilters.statuses)
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        <ShowAllOnMapToggle
          checked={showAllAssets}
          onChange={onShowAllAssetsChange}
          ariaLabel="Show all filtered assets on the map"
        />
        <FilterDropdown
          label="Kind"
          options={ASSET_KIND_ORDER.map((kind) => ({
            value: kind,
            label: kind === 'assetTracker' ? 'Asset tracker' : 'Vehicle',
            swatchColor: ASSET_KIND_COLOR[kind],
          }))}
          selected={assetFilters.kinds}
          onToggle={(value) =>
            onAssetFiltersChange({
              ...assetFilters,
              kinds: toggleInList(
                assetFilters.kinds,
                value as (typeof assetFilters.kinds)[number],
              ),
            })
          }
        />
        {statuses.length > 0 ? (
          <FilterDropdown
            label="Status"
            options={statuses.map((status) => ({
              value: status,
              label: status,
            }))}
            selected={assetFilters.statuses}
            onToggle={(value) =>
              onAssetFiltersChange({
                ...assetFilters,
                statuses: toggleInList(assetFilters.statuses, value),
              })
            }
          />
        ) : null}
      </div>
    )
  }

  const states = mergedOptions(municipalityStates, municipalityFilters.states)
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <label
        className={`btn btn-xs no-animation gap-1.5 normal-case ${
          boundaryShowLevel !== 'none' ? 'btn-primary' : 'btn-ghost border border-base-300'
        }`}
      >
        Show all
        <select
          className="select select-ghost select-xs h-5 min-h-0 w-[5.75rem] border-0 bg-transparent p-0 pr-5 font-medium leading-none"
          value={boundaryShowLevel}
          onChange={(e) => onBoundaryShowLevelChange(e.target.value as BoundaryShowAllLevel)}
          aria-label="Show all boundary areas"
        >
          {BOUNDARY_SHOW_ALL_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>
      {boundaryOverlaysLoading ? (
        <p className="inline-flex items-center gap-1 text-[10px] text-base-content/50">
          <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
          Loading areas…
        </p>
      ) : null}
      {states.length > 0 ? (
        <FilterDropdown
          label="State"
          options={states.map((state) => ({
            value: state,
            label: state,
          }))}
          selected={municipalityFilters.states}
          onToggle={(value) =>
            onMunicipalityFiltersChange({
              ...municipalityFilters,
              states: toggleInList(municipalityFilters.states, value),
            })
          }
        />
      ) : null}
      <FilterDropdown
        label="Level"
        options={MUNICIPALITY_LEVEL_ORDER.map((level) => ({
          value: level,
          label: titleCase(level),
          swatchColor: MUNICIPALITY_LEVEL_COLOR[level],
        }))}
        selected={municipalityFilters.levels}
        onToggle={(value) =>
          onMunicipalityFiltersChange({
            ...municipalityFilters,
            levels: toggleInList(
              municipalityFilters.levels,
              value as (typeof municipalityFilters.levels)[number],
            ),
          })
        }
      />
      <FilterDropdown
        label="License"
        options={LICENSE_TYPE_ORDER.map((type) => ({
          value: type,
          label: licenseTypeLabel(type),
          swatchColor: LICENSE_TYPE_COLOR[type],
        }))}
        selected={municipalityFilters.licenseTypes}
        onToggle={(value) =>
          onMunicipalityFiltersChange({
            ...municipalityFilters,
            licenseTypes: toggleInList(
              municipalityFilters.licenseTypes,
              value as (typeof municipalityFilters.licenseTypes)[number],
            ),
          })
        }
      />
      <FilterDropdown
        label="Status"
        options={[
          { value: 'current', label: 'Current' },
          { value: 'expired', label: 'Expired' },
          { value: 'expiring', label: 'Expiring ≤90 days' },
        ]}
        selected={municipalityFilters.statuses}
        onToggle={(value) =>
          onMunicipalityFiltersChange({
            ...municipalityFilters,
            statuses: toggleInList(
              municipalityFilters.statuses,
              value as (typeof municipalityFilters.statuses)[number],
            ),
          })
        }
      />
    </div>
  )
}
